import {
  AdminMaintenanceRoutes,
  AdminRouteMetadata,
  IRouteMetadata,
  RouteMetadata,
  UserMedicineRoutes,
  UserRiskAssessmentRoutes,
  UserStorageRoutes,
  UserTasksRoutes,
} from 'src/app/core/enums/routes.enum';
/* eslint-disable no-magic-numbers */
/* eslint-disable prefer-destructuring */
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { IdName } from 'src/app/shared/models/id-name';
import {
  UserCertificateRoutes,
  UserDocumentsRoutes,
  UserDrillsRoutes,
  UserMaintenanceRoutes,
  UserRunTimeRoutes,
} from '../enums/routes.enum';
import {
  MainModulesEnum,
  DepartmentSubModulesEnum,
  CompetencesSubModulesEnum,
  NonConformanceSubModulesEnum,
} from '../enums/modules.enum';
import { MenuStateModel } from '../state/menu-list.state';
import { MenuItemModel, MenuItemWithIndent } from '../models/menu-id.model';
import { ModuleNumbers } from '../enums/module-numbers.enum';

export interface MenuModuleRoutes {
  selectedRoot: string;
  viewAllRoot: string;
  appendNodeId: boolean;
  preserveNestedRoute: boolean;
  defaultChildRoute?: string;
  preservedQueryParams?: string[];
}

type MenuModuleRouteOptions = Partial<Omit<MenuModuleRoutes, 'selectedRoot' | 'viewAllRoot'>>;

@Injectable({
  providedIn: 'root',
})
export class MenuListLogicService {
  private urlParts: string[];
  private readonly moduleRoutes = new Map<ModuleNumbers, MenuModuleRoutes>([
    [
      ModuleNumbers.Certificate,
      this.createModuleRoutes(UserCertificateRoutes.Overview, UserCertificateRoutes.Main, {
        defaultChildRoute: 'active',
      }),
    ],
    [
      ModuleNumbers.Maintenance,
      this.createModuleRoutes(UserMaintenanceRoutes.Component, UserMaintenanceRoutes.Main, {
        preserveNestedRoute: false,
        preservedQueryParams: ['activeTab'],
      }),
    ],
    [
      ModuleNumbers.Document,
      this.createModuleRoutes(UserDocumentsRoutes.Overview, UserDocumentsRoutes.Main, {
        defaultChildRoute: 'published',
      }),
    ],
    [
      ModuleNumbers.Storage,
      this.createModuleRoutes(UserStorageRoutes.List, UserStorageRoutes.Main),
    ],
    [ModuleNumbers.Competence, this.createModuleRoutes('', '', { appendNodeId: false })],
    [
      ModuleNumbers.Medicine,
      this.createModuleRoutes(UserMedicineRoutes.List, UserMedicineRoutes.Main),
    ],
    [
      ModuleNumbers.Drill,
      this.createModuleRoutes(UserDrillsRoutes.Overview, UserDrillsRoutes.Main, {
        defaultChildRoute: 'active',
      }),
    ],
    [
      ModuleNumbers.Task,
      this.createModuleRoutes(UserTasksRoutes.Overview, UserTasksRoutes.Main, {
        defaultChildRoute: 'active',
      }),
    ],
    [
      ModuleNumbers.RiskAssessment,
      this.createModuleRoutes(UserRiskAssessmentRoutes.List, UserRiskAssessmentRoutes.Main),
    ],
    [
      ModuleNumbers.StandardJobTemplates,
      this.createModuleRoutes(
        AdminMaintenanceRoutes.StandardJobTemplatesRoot,
        AdminMaintenanceRoutes.StandardJobTemplatesRoot
      ),
    ],
    [
      ModuleNumbers.Runtime,
      this.createModuleRoutes(UserRunTimeRoutes.Main, UserRunTimeRoutes.Main, {
        appendNodeId: false,
      }),
    ],
  ]);
  private tempParentId = '';

  constructor(private router: Router) {}

  saveTempParentId(id: string): void {
    this.tempParentId = id;
  }

  getParentId(): string {
    return this.tempParentId;
  }

  getIdNameFlatListOfPossibleLocations(menuItems: MenuItemModel[]): IdName[] {
    let idNameList: IdName[] = [];
    if (menuItems) {
      menuItems.forEach((item: MenuItemModel) => {
        idNameList.push({ id: item.id, name: item.name });
        if (item.children && item.children.length > 0) {
          idNameList = idNameList.concat(this.getIdNameFlatListOfPossibleLocations(item.children));
        }
      });
    }
    return idNameList;
  }

  getIdNameIndentListOfPossibleLocations(
    menuItems: MenuItemModel[],
    level = 0
  ): MenuItemWithIndent[] {
    let idNameList: MenuItemWithIndent[] = [];
    if (menuItems) {
      menuItems.forEach((item: MenuItemModel) => {
        idNameList.push({
          id: item.id,
          name: item.name,
          indent: level,
          parentId: item.parentId,
        });
        if (item.children && item.children.length > 0) {
          idNameList = idNameList.concat(
            this.getIdNameIndentListOfPossibleLocations(item.children, level + 1)
          );
        }
      });
    }
    return idNameList;
  }

  getMenuItemLocation(menuItem: MenuItemWithIndent, allMenuItems: MenuItemWithIndent[]): string {
    const pathParts: string[] = [menuItem.name];
    const buildPath = (item: MenuItemWithIndent): void => {
      if (item.parentId) {
        const parentItem = allMenuItems.find((potential) => potential.id === item.parentId);
        if (parentItem) {
          pathParts.unshift(parentItem.name);
          buildPath(parentItem);
        }
      }
    };

    buildPath(menuItem);
    return pathParts.join(' / ');
  }

  getModuleRoutes(moduleNumber: number): MenuModuleRoutes | undefined {
    return this.moduleRoutes.get(moduleNumber);
  }

  getModuleNumberFromUrl(url: string): number | undefined {
    const metadata = this.getModuleMetadata(url);
    return metadata?.number ?? undefined;
  }

  getModuleNameFromUrl(url: string): string | undefined {
    const metadata = this.getModuleMetadata(url);
    return metadata?.name ?? undefined;
  }

  getModuleMetadata(url: string): IRouteMetadata | null {
    const urlPath = this.getUrlPath(url);
    const metadata = urlPath.includes('admin') ? AdminRouteMetadata : RouteMetadata;
    const closestMatch = Object.keys(metadata)
      .filter((key) => urlPath.includes(key))
      .sort((a, b) => b.length - a.length)[0];
    return closestMatch ? metadata[closestMatch] : null;
  }

  getMenuStateModel(url: string): MenuStateModel {
    const urlPath = this.getUrlPath(url);
    const urlParts = urlPath.split('/');
    this.urlParts = urlParts;
    let mainModule = '';
    let subModule = '';
    let isInAdmin = false;

    if (urlParts[1] === MainModulesEnum.Admin) {
      // first we check whether first part of the array is the ADMIN module
      isInAdmin = true;
      if (this.urlFragmentPartOfMainModuleEnum(urlParts[2])) {
        // next we check if a main module can be found in 2nd element
        mainModule = urlParts[2];
      } else if (this.urlFragmentPartOfMainModuleEnum(urlParts[3])) {
        // then we check for main module in 3rd element, because sometimes
        // 2nd element is a "tenantId"
        mainModule = urlParts[3];
      }
    } else if (this.urlFragmentPartOfMainModuleEnum(urlParts[1])) {
      // not in admin, then we check for main module in 1st element
      mainModule = urlParts[1];
    } else if (this.urlFragmentPartOfMainModuleEnum(urlParts[2])) {
      // not in admin, then we check again for main module but in 2nd element
      // because sometimes 1st element can be "tenantId"
      mainModule = urlParts[2];
    }

    switch (mainModule) {
      case MainModulesEnum.Department:
        subModule = this.getSubModuleForEnum(DepartmentSubModulesEnum);
        break;
      case MainModulesEnum.Competence:
        subModule = this.getSubModuleForEnum(CompetencesSubModulesEnum);
        break;
      case MainModulesEnum.NonConformance:
        subModule = this.getSubModuleForEnum(NonConformanceSubModulesEnum);
        break;
      default:
        break;
    }

    return {
      mainModule,
      subModule,
      isInAdmin,
    };
  }

  private urlFragmentPartOfMainModuleEnum(urlFragment: string): boolean {
    return (<any>Object).values(MainModulesEnum).includes(urlFragment);
  }

  private getSubModuleForEnum(e: any): string {
    let subModule = '';
    this.urlParts.forEach((urlFragment: string) => {
      if ((<any>Object).values(e).includes(urlFragment)) {
        subModule = urlFragment;
      }
    });
    return subModule;
  }

  getCurrentUrl(): string {
    return this.router.url;
  }

  isCurrentUrlInAdmin(): boolean {
    const currentUrl = this.getUrlPath(this.getCurrentUrl());
    const urlParts = currentUrl.split('/');
    return urlParts[1] === MainModulesEnum.Admin;
  }

  private getUrlPath(url: string): string {
    return url.split(/[?#]/)[0];
  }

  private createModuleRoutes(
    selectedRoot: string,
    viewAllRoot: string,
    options: MenuModuleRouteOptions = {}
  ): MenuModuleRoutes {
    const { appendNodeId = true, preserveNestedRoute = true, ...routeOptions } = options;
    return { selectedRoot, viewAllRoot, appendNodeId, preserveNestedRoute, ...routeOptions };
  }
}
